import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { PickingsService } from '@app/services/pickings/picking.service';
import { SubSink } from 'subsink';
import { StorageOverrideDialogComponent } from '@app/shared/components/storage-override-dialog/storage-override-dialog.component';
import { QuickPickDialogComponent } from '@app/shared/components/quick-pick-dialog/quick-pick-dialog.component';
import { QuickReceiveDialogComponent } from '@app/shared/components/quick-receive-dialog/quick-receive-dialog.component';
import { ReceivingAddComponent } from '@app/modules/receivings/receiving-add/receiving-add.component';
import { environment } from 'environments/environment';
import { ImageUploadService } from '@app/services/image-upload/image-upload.service';
import { LabelPrintMenuService } from '@app/services/labels/label-print-menu.service';

const dymoUrl = 'assets/scripts/printerCode.js?v9';
const brotherUrl = environment.apiUrl.replace('/api','') + '/assets/brother/brotherPrinterCode.js';

export interface ProductActivityData {
  prdID: number;
  prdNameEn: string;
  prdCode: string;
  // Printer ayarları
  locEnablePrinting?: number;
  locEnableBrotherPrinting?: number;
  locPrdCustomFieldLabel1?: string;
  locPrdCustomFieldLabel2?: string;
  prdCustomField1?: string;
  prdCustomField2?: string;
  customFieldPrintable?: number;
  countType?: any[];
  barcodes?: any[];
  prdCasePack?: string;
  prdAssetsNumber?: string;
  prdIsAsset?: number;
  prdIsSerialized?: number;
}

export interface StorageActivity {
  storageStructureID: number;
  storagePath: string;
  onHand: number;
  lastCount: number | null;
  activities: Activity[];
}

export interface Activity {
  time: string;
  action: string;
  user: string;
  quantity: number;
  note?: string;
  serialNumbers?: string[];
}

@Component({
  selector: 'app-product-activity-dialog',
  templateUrl: './product-activity-dialog.component.html',
  styleUrls: ['./product-activity-dialog.component.scss']
})
export class ProductActivityDialogComponent implements OnInit, OnDestroy {
  public storages: StorageActivity[] = [];
  public isLoading: boolean = true;
  public subs: SubSink = new SubSink();
  public expandedStorages: Set<number> = new Set();
  private activeStorageID: number | null = null;
  private hasChanges: boolean = false;

  // Printer variables
  public hasDymoPrinter: boolean = false;
  public hasBrotherPrinter: boolean = false;
  public defaultCount: any;
  public printLabelList = [
    {
      "DYMO": [
        {
          name: 'Dunnage Shelf Label (2 1/8" x 1")',
          value: 0,
          templateFile : ''
        },
        {
          name: 'Price Tag Label (2" x 3/4")',
          value: 2,
          templateFile : ''
        },
        {
          name: 'Shelf Label (3 1/2" x 1 1/8") - QR Code',
          value: 5,
          templateFile : ''
        },
        {
          name: 'Shipping Label (2 1/8" x 4") - QR (Assets)',
          value: 7,
          templateFile : ''
        },
        {
          name: 'Shipping Label (2 1/8" x 4") - Barcode (Products)',
          value: 8,
          templateFile : ''
        }
      ]
    },
    {
      "BROTHER (Windows only)": [
        {
          name: '12mm 5" SKU & QR code',
          value: 0,
          templateFile : 'shelfLabel_DSAQR_12x5_sku_qr.lbx'
        },
        {
          name: '12mm 5" SKUCUSTOM1 & QR code',
          value: 0,
          templateFile : 'shelfLabel_DSAQR_12x5_sku_c1_qr.lbx'
        },
        {
          name: '12mm 5" Description, SKU & QR code',
          value: 0,
          templateFile : 'shelfLabel_DSAQR_12x5_desc_sku.lbx'
        },
        {
          name: '12mm 5" Description, SKUCUSTOM1 & QR code',
          value: 0,
          templateFile : 'shelfLabel_DSAQR_12x5_desc_sku_c1.lbx'
        },
        {
          name: '12mm 5" Description, SKUCUSTOM1CUSTOM2 & QR code',
          value: 0,
          templateFile : 'shelfLabel_DSAQR_12x5_desc_sku_c1_c2.lbx'
        },
        {
          name: '24mm 4" Description, SKUCUSTOM1 & QR code',
          value: 4,
          templateFile : 'shelfLabel_DASQR_24x4.lbx'
        },
        {
          name: '12mm 4" QR code, SKUCUSTOM1 & Description',
          value: 7,
          templateFile : 'shelfLabel_DASQR_12x4v1.lbx'
        },
        {
          name: '12mm 4" QR code, SKUCUSTOM1 & Description (Dynamic)',
          value: 6,
          templateFile : 'shelfLabel_DASQR_12x4.lbx'
        },
        {
          name: '36mm 1.5" Description, SKUCUSTOM1 & QR code',
          value: 5,
          templateFile : 'shelfLabel_DABQR_36x1p5.lbx'
        }
      ]
    }
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ProductActivityData,
    private _dialogRef: MatDialogRef<ProductActivityDialogComponent>,
    private _pickingService: PickingsService,
    private _matDialog: MatDialog,
    private _imageUpload: ImageUploadService,
    private _labelMenu: LabelPrintMenuService
  ) {}

  ngOnInit(): void {
    this.loadProductActivity();
    this.initPrinters();
    this._imageUpload.cacheLogoForLabelPrinting();

    // The customer's own designed labels join the built-in DYMO ones.
    this._labelMenu.appendTo(this.printLabelList).subscribe();

    // Catch closing by backdrop click and by the ESC key
    this._dialogRef.backdropClick().subscribe(() => {
      this.onClose();
    });

    this._dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.onClose();
      }
    });
  }

  private initPrinters(): void {
    // Filter printLabelList based on custom field labels
    const locPrdCustomFieldLabel1 = this.data.locPrdCustomFieldLabel1;
    const locPrdCustomFieldLabel2 = this.data.locPrdCustomFieldLabel2;

    this.printLabelList.forEach(printer => {
      Object.keys(printer).forEach(key => {
        if (locPrdCustomFieldLabel1 === null || locPrdCustomFieldLabel1 === '' || locPrdCustomFieldLabel1 === undefined) {
          printer[key] = printer[key].filter(label => !label.name.includes('12mm 5" SKUCUSTOM1 & QR code') && !label.name.includes('12mm 5" Description, SKUCUSTOM1 & QR code'));
        }
        if (locPrdCustomFieldLabel2 === null || locPrdCustomFieldLabel2 === '' || locPrdCustomFieldLabel2 === undefined) {
          printer[key] = printer[key].filter(label => !label.name.includes('CUSTOM2'));
        }
        printer[key].forEach(label => {
          if (label.name.includes('CUSTOM1')) {
            label.name = label.name.replace('CUSTOM1', (locPrdCustomFieldLabel1 != null ? (', ' + locPrdCustomFieldLabel1) : ''));
          }
          if (label.name.includes('CUSTOM2') && locPrdCustomFieldLabel2 !== null) {
            label.name = label.name.replace('CUSTOM2', (locPrdCustomFieldLabel2 != null ? (', ' + locPrdCustomFieldLabel2) : ''));
          }
        });
      });
    });

    // Load printer scripts
    this.loadDymoScript();
    this.loadBrotherScript();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  loadProductActivity(refreshStorageID?: number): void {
    const isRefresh = this.storages.length > 0;
    if (!isRefresh) {
      this.isLoading = true;
    }
    this.activeStorageID = refreshStorageID || null;

    this.subs.add(
      this._pickingService.getProductActivity(this.data.prdID).subscribe({
        next: (response) => {
          this.storages = response.storages || [];
          if (!isRefresh) {
            // First load: expand all panels
            this.storages.forEach(s => this.expandedStorages.add(s.storageStructureID));
          }
          this.isLoading = false;

          // Scroll to the storage that was acted upon
          if (this.activeStorageID) {
            setTimeout(() => this.scrollToStorage(this.activeStorageID));
          }
        },
        error: (error) => {
          console.error('Error loading product activity:', error);
          this.isLoading = false;
        }
      })
    );
  }

  openOverride(storage: StorageActivity): void {
    const dialogRef = this._matDialog.open(StorageOverrideDialogComponent, {
      data: {
        prdID: this.data.prdID,
        storageStructureID: storage.storageStructureID,
        storagePath: storage.storagePath,
        currentOnHand: storage.onHand,
        prdNameEn: this.data.prdNameEn,
        prdIsSerialized: this.data.prdIsSerialized
      },
      minWidth: '400px',
      maxWidth: '600px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.hasChanges = true;
        this.loadProductActivity(storage.storageStructureID);
      }
    });
  }

  openQuickPick(storage: StorageActivity): void {
    const dialogRef = this._matDialog.open(QuickPickDialogComponent, {
      data: {
        prdID: this.data.prdID,
        storageStructureID: storage.storageStructureID,
        storagePath: storage.storagePath,
        currentOnHand: storage.onHand,
        prdNameEn: this.data.prdNameEn,
        prdIsSerialized: this.data.prdIsSerialized
      },
      minWidth: '400px',
      maxWidth: '600px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.hasChanges = true;
        this.loadProductActivity(storage.storageStructureID);
      }
    });
  }

  openQuickReceive(storage: StorageActivity): void {
    const dialogRef = this._matDialog.open(QuickReceiveDialogComponent, {
      data: {
        prdID: this.data.prdID,
        storageStructureID: storage.storageStructureID,
        storagePath: storage.storagePath,
        currentOnHand: storage.onHand,
        prdNameEn: this.data.prdNameEn,
        prdIsSerialized: this.data.prdIsSerialized
      },
      minWidth: '400px',
      maxWidth: '600px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.hasChanges = true;
        this.loadProductActivity(storage.storageStructureID);
      }
    });
  }

  // Storage-less products (placeholder membership) have no storage rows, so
  // the per-storage quick receive is unavailable. This opens the full receive
  // dialog with the product preselected; the user picks the storage there.
  openReceiveIntoStorage(): void {
    const dialogRef = this._matDialog.open(ReceivingAddComponent, {
      minWidth: '40vw',
      minHeight: '400px',
      maxWidth: '',
      maxHeight: '80vh',
      panelClass: 'ReceivingAdd__Modal',
      data: {
        prdCode: this.data.prdCode,
        prdNameEn: this.data.prdNameEn,
        action: 'product_receiving',
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.hasChanges = true;
      this.loadProductActivity();
    });
  }

  onClose(): void {
    this._dialogRef.close(this.hasChanges);
  }

  getActionClass(action: string): string {
    switch (action) {
      case 'Counted':
        return 'action-counted';
      case 'Picked':
        return 'action-picked';
      case 'Received':
        return 'action-received';
      case 'Adjusted':
        return 'action-adjusted';
      default:
        return '';
    }
  }

  getQtyClass(action: string): string {
    switch (action) {
      case 'Counted':
        return 'qty-counted';
      case 'Picked':
        return 'qty-picked';
      case 'Received':
        return 'qty-received';
      case 'Adjusted':
        return 'qty-adjusted';
      default:
        return '';
    }
  }

  onPanelToggle(storageStructureID: number, expanded: boolean): void {
    if (expanded) {
      this.expandedStorages.add(storageStructureID);
    } else {
      this.expandedStorages.delete(storageStructureID);
    }
  }

  isExpanded(storageStructureID: number): boolean {
    return this.expandedStorages.has(storageStructureID);
  }

  trackByStorageId(index: number, storage: StorageActivity): number {
    return storage.storageStructureID;
  }

  private scrollToStorage(storageStructureID: number): void {
    const el = document.getElementById('storage-' + storageStructureID);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // Printer Methods
  public loadDymoScript(): void {
    let node = document.createElement('script');
    node.src = dymoUrl;
    node.type = 'text/javascript';
    node.async = true;
    node.charset = 'utf-8';
    document.getElementsByTagName('head')[0].appendChild(node);
    this.hasDymoPrinter = true;
  }

  public loadBrotherScript(): void {
    let node = document.createElement('script');
    node.src = brotherUrl;
    node.type = 'text/javascript';
    node.setAttribute('type', 'module');
    node.async = true;
    node.charset = 'utf-8';
    document.getElementsByTagName('head')[0].appendChild(node);
    this.hasBrotherPrinter = true;
  }

  onSelectLabel(event: any, printerType: string, labelValue: number | string, storageStructureID: number): void {
    if (printerType === 'DYMO') {
      localStorage.setItem('labelType', String(labelValue));
      localStorage.setItem('storageStructureID', storageStructureID.toString());
      localStorage.setItem('storagePrint', 'false');
      this.setDefaultCount();
    }
  }

  private setDefaultCount(): void {
    const selected = this.data.barcodes?.find(item => item.selected === true);
    this.defaultCount = this.data.countType?.find(item => item.pcmIsDefaultCountMethod === 1);
    localStorage.setItem('prdRetail', this.defaultCount?.pcmRetail || '');
    localStorage.setItem('prdCost', this.defaultCount?.pcmCost || '');
    localStorage.setItem('prdBarcode', selected?.barcodeData || '');
    localStorage.setItem('prdNameEn', this.data.prdNameEn || '');
    localStorage.setItem('prdCode', this.data.prdCode || '');
    localStorage.setItem('prdCasePack', this.data.prdCasePack || '');
    localStorage.setItem('prdID', this.data.prdID?.toString() || '');
    localStorage.setItem('prdAssetsNumber', this.data.prdAssetsNumber || '');
    localStorage.setItem('prdIsAsset', this.data.prdIsAsset ? '1' : '0');

    const printSelectedDiv = document.getElementById('dymoPrintSelected');
    if (printSelectedDiv) {
      printSelectedDiv.click();
    }
  }
}
